import Bind from "lodash-decorators/bind";
import React, {
    Component,
    ComponentClass,
    ComponentType,
    FocusEvent,
    MutableRefObject,
    Ref,
    RefCallback,
} from "react";
import Popup, { PopupProps } from "../popup/popup";
import { forwardRef, WithForwardedRef } from "../utils/ref";
import { WithValidationProps } from "./with_validation";

const noop = () => {
    /* ignore */
};

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>): RefCallback<T> {
    return (value) => {
        refs.forEach((ref) => {
            if (typeof ref === "function") {
                ref(value);
            } else if (ref != null) {
                (ref as MutableRefObject<T | null>).current = value;
            }
        });
    };
}

export interface WithValidationPopupRequiredProps {
    onFocus?(e: FocusEvent<any>): void;
    onBlur?(e: FocusEvent<any>): void;
}

interface State {
    error?: string;
    asyncStatus?: "pending" | "success" | "error";
    popupActive: boolean;
}

export interface Options<T> {
    uiAsyncValidationStatusProp?: keyof T;
    uiErrorProp?: keyof T;
}

export interface WithValidationErrorPopupProps {
    id: string;
    uiPopupPosition?: PopupProps["uiPosition"];
    uiPopupInverted?: PopupProps["uiInverted"];
    uiPopupOffset?: PopupProps["uiOffset"];
    uiPopupDistanceAway?: PopupProps["uiDistanceAway"];
    uiReportAsyncValidationPending?: boolean;
    uiReportAsyncValidationResult?: boolean;
    uiReportResultTimeout?: number;
    uiHideErrorOnFocus?: boolean;
    uiError?: boolean;
    uiPopupComponent?: ComponentType<PopupProps>;
    uiRenderErrorMessage?(message: string): string | JSX.Element;
    popupString?: boolean;
    name?: string;
    value?: string | number;
    handleEmptyInputPassword?(): void;
    required?: boolean;
    uiValidityMessages?: any;
    uiHideOnClickOutside?: boolean;
}

export function WithValidationErrorPopup<T extends WithValidationProps>(
    WrappedComponent: React.ComponentType<T & WithValidationPopupRequiredProps>,
    options: Options<T> = {}
): React.FC<T & WithValidationErrorPopupProps> {
    class WithErrorMessageComponent extends Component<
        WithValidationProps & WithValidationErrorPopupProps & WithValidationPopupRequiredProps,
        State
    > {
        private wrapperRef = React.createRef<any>();
        private popupRef = React.createRef<any>();

        public static displayName = `WithErrorMessage(${
            WrappedComponent.displayName || WrappedComponent.name || "WrappedComponent"
        })`;

        public static defaultProps: Partial<
            WithValidationProps & WithValidationErrorPopupProps & WithValidationPopupRequiredProps
        > = {
            uiRenderErrorMessage: (err) => err,
            uiPopupDistanceAway: 10,
            uiHideErrorOnFocus: true,
        };

        public state: State = {
            error: undefined,
            asyncStatus: undefined,
            popupActive: false,
        };

        private statusTimer?: number;

        @Bind()
        private handleClickOutside(event: MouseEvent): void {
            if (
                this.state.popupActive &&
                this.wrapperRef.current &&
                !this.wrapperRef.current.contains(event.target as Node)
            ) {
                this.setState({ popupActive: false });
            }
        }

        public componentDidMount(): void {
            if (this.props.uiHideOnClickOutside) {
                document.addEventListener("mousedown", this.handleClickOutside);
            }
        }

        public componentWillUnmount(): void {
            if (this.props.uiHideOnClickOutside) {
                document.removeEventListener("mousedown", this.handleClickOutside);
            }
            if (this.statusTimer) {
                window.clearTimeout(this.statusTimer);
            }
        }

        public render(): JSX.Element {
            const { asyncStatus, popupActive } = this.state;
            const additionalProps: Record<string, unknown> = {
                uiError: false,
            };

            if (options.uiAsyncValidationStatusProp) {
                if (asyncStatus === "pending" && this.props.uiReportAsyncValidationPending) {
                    additionalProps[options.uiAsyncValidationStatusProp as string] = asyncStatus;
                } else if (
                    (asyncStatus === "success" || asyncStatus === "error") &&
                    this.props.uiReportAsyncValidationResult
                ) {
                    additionalProps[options.uiAsyncValidationStatusProp as string] = asyncStatus;
                }
            }

            if (options.uiErrorProp) {
                additionalProps[options.uiErrorProp as string] = !!this.state.error;
            }

            const {
                uiReportAsyncValidationPending,
                uiReportAsyncValidationResult,
                uiReportResultTimeout,
                forwardedRef,
                uiPopupDistanceAway,
                uiPopupInverted,
                uiPopupOffset,
                uiPopupPosition,
                uiRenderErrorMessage,
                uiPopupComponent,
                ...other
            } = this.props as WithForwardedRef<WithValidationErrorPopupProps & WithValidationProps>;

            const Comp =
                WrappedComponent as any as ComponentClass<
                    WithValidationProps & WithValidationPopupRequiredProps
                >;
            const PopupComponent = uiPopupComponent || Popup;

            const customErrorMessage =
                (this.props.uiValidityMessages && this.props.uiValidityMessages.patternMismatch) ||
                (this.props.uiValidityMessages && this.props.uiValidityMessages.valueMissing);

            return (
                <>
                    <Comp
                        {...(additionalProps as unknown as T)}
                        {...other}
                        uiOnValid={this.onValid}
                        uiOnInvalid={this.onInvalid}
                        uiOnStartAsyncValidation={this.onStartAsyncValidation}
                        uiOnFinishAsyncValidation={this.onFinishAsyncValidation}
                        ref={mergeRefs(this.wrapperRef, forwardedRef)}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                    />

                    {["cost", "msrp"].includes(this.props.id)
                        ? !customErrorMessage &&
                          !this.props.popupString &&
                          this.props.required && (
                              <PopupComponent
                                  ref={this.popupRef}
                                  uiActive={popupActive}
                                  uiTargetEl={this.props.id}
                                  uiOnRequestClose={noop}
                                  uiAutoPosition
                                  uiPosition={uiPopupPosition}
                                  uiInverted={uiPopupInverted}
                                  uiOffset={uiPopupOffset}
                                  uiDistanceAway={uiPopupDistanceAway}
                              >
                                  {this.state.error && uiRenderErrorMessage!(this.state.error)}
                              </PopupComponent>
                          )
                        : !this.props.popupString &&
                          !customErrorMessage && (
                              <PopupComponent
                                  uiActive={popupActive}
                                  uiTargetEl={this.props.id}
                                  uiOnRequestClose={noop}
                                  uiAutoPosition
                                  uiPosition={uiPopupPosition}
                                  uiInverted={uiPopupInverted}
                                  uiOffset={uiPopupOffset}
                                  uiDistanceAway={uiPopupDistanceAway}
                              >
                                  {this.state.error && uiRenderErrorMessage!(this.state.error)}
                              </PopupComponent>
                          )}

                    {customErrorMessage &&
                        customErrorMessage.length > 0 &&
                        this.props.required &&
                        !this.props.value &&
                        additionalProps &&
                        additionalProps.uiError && (
                            <PopupComponent
                                uiActive={popupActive}
                                uiTargetEl={this.props.id}
                                uiOnRequestClose={noop}
                                uiAutoPosition
                                uiPosition={uiPopupPosition}
                                uiInverted={uiPopupInverted}
                                uiOffset={uiPopupOffset}
                                uiDistanceAway={uiPopupDistanceAway}
                            >
                                {customErrorMessage && uiRenderErrorMessage!(customErrorMessage)}
                            </PopupComponent>
                        )}
                </>
            );
        }

        @Bind()
        private onBlur(e: FocusEvent<any>): void {
            const { onBlur, uiHideErrorOnFocus } = this.props;
            if (uiHideErrorOnFocus && this.state.error && !this.state.popupActive) {
                this.setState({
                    popupActive: true,
                });
            }
            if (onBlur) {
                onBlur(e);
            }
        }

        @Bind()
        private onFocus(e: FocusEvent<any>): void {
            const { onFocus, uiHideErrorOnFocus } = this.props;

            if (uiHideErrorOnFocus) {
                this.setState({ popupActive: false });
            }
            if (
                [
                    "Markup Cost Trade Input",
                    "Discount MSRP Trade Input",
                    "Rate Trade Input",
                    "Item MSRP",
                    "Item Cost",
                    "Markup Cost Retail Input",
                    "Discount MSRP Retail Input",
                    "Rate Retail Input",
                    "Shipping Cost Input",
                    "Handling Fee MarkUp Product Input",
                    "Handling Fee MarkUp Shipping Cost Input",
                    "Handling Fee Rate Input",
                    "Item Name",
                    "Cost Plus Retail Input",
                ].includes((e.target as HTMLInputElement).name)
            ) {
                this.setState({ popupActive: true });
            }
            if (onFocus) {
                onFocus(e);
            }
        }

        @Bind()
        private onValid(): void {
            if (this.state.error) {
                this.setState({ error: undefined, popupActive: false });
            }
        }

        @Bind()
        private onInvalid(msg: string, el: any, val: any): void {
            const resolvedMessage = this.resolveErrorMessage(msg, el);
            if (
                ![
                    "Handling Fee MarkUp Product Input",
                    "Handling Fee MarkUp Shipping Cost Input",
                    "Handling Fee Rate Input",
                    "Item MSRP",
                    "Item Cost",
                    "Shipping Cost Input",
                ].includes(el.name)
            ) {
                if (!this.props.uiHideOnClickOutside) {
                    el.focus();
                }
            }
            if (el.name === "password" && this.props.handleEmptyInputPassword && val === undefined) {
                this.props.handleEmptyInputPassword();
            }
            if (
                (!this.state.error || this.state.error !== resolvedMessage) &&
                resolvedMessage !== "Please select billing type for client."
            ) {
                this.setState({ error: resolvedMessage, popupActive: true });
            } else if (resolvedMessage === "Please select billing type for client.") {
                this.setState({
                    error: "Please select billing type for client",
                    popupActive: true,
                });
            }
        }

        @Bind()
        private onStartAsyncValidation(): void {
            if (this.statusTimer) {
                window.clearTimeout(this.statusTimer);
            }
            if (this.props.uiReportAsyncValidationPending) {
                this.setState({ asyncStatus: "pending" });
            }
        }

        @Bind()
        private onFinishAsyncValidation(valid: boolean): void {
            const {
                uiReportAsyncValidationResult,
                uiReportAsyncValidationPending,
                uiReportResultTimeout,
            } = this.props;

            if (uiReportAsyncValidationPending || uiReportAsyncValidationResult) {
                this.setState({
                    asyncStatus: !uiReportAsyncValidationResult ? undefined : valid ? "success" : "error",
                });
            }
            if (uiReportResultTimeout && uiReportAsyncValidationResult) {
                this.statusTimer = window.setTimeout(() => {
                    this.statusTimer = undefined;
                    this.setState({ asyncStatus: undefined });
                }, uiReportResultTimeout);
            }
        }

        private resolveErrorMessage(msg: string, el: HTMLInputElement): string {
            if (msg && msg.trim()) {
                return msg;
            }

            const { uiValidityMessages } = this.props as any;
            const validity = el.validity;

            const findFromMessages = (key: string): string | undefined =>
                uiValidityMessages && typeof uiValidityMessages === "object"
                    ? uiValidityMessages[key]
                    : undefined;

            if (validity) {
                if (validity.valueMissing) {
                    const fallback =
                        findFromMessages("valueMissing") ||
                        el.getAttribute("data-required-message") ||
                        "Please fill out this field.";
                    el.setCustomValidity(fallback);
                    return fallback;
                }
                if (validity.typeMismatch) {
                    const fallback =
                        findFromMessages("typeMismatch") ||
                        (el.type === "email"
                            ? "Invalid email"
                            : el.type === "url"
                                ? "Please enter a valid URL."
                                : "Please enter a valid value.");
                    el.setCustomValidity(fallback);
                    return fallback;
                }
                if (validity.patternMismatch) {
                    const fallback =
                        findFromMessages("patternMismatch") ||
                        "Please match the requested format.";
                    el.setCustomValidity(fallback);
                    return fallback;
                }
                if (validity.tooShort) {
                    const fallback =
                        findFromMessages("tooShort") ||
                        `Please lengthen this text to ${el.minLength} characters or more.`;
                    el.setCustomValidity(fallback);
                    return fallback;
                }
                if (validity.tooLong) {
                    const fallback =
                        findFromMessages("tooLong") ||
                        `Please shorten this text to no more than ${el.maxLength} characters.`;
                    el.setCustomValidity(fallback);
                    return fallback;
                }
                if (validity.rangeUnderflow) {
                    const fallback =
                        findFromMessages("rangeUnderflow") ||
                        `Value must be greater than or equal to ${el.min}.`;
                    el.setCustomValidity(fallback);
                    return fallback;
                }
                if (validity.rangeOverflow) {
                    const fallback =
                        findFromMessages("rangeOverflow") ||
                        `Value must be less than or equal to ${el.max}.`;
                    el.setCustomValidity(fallback);
                    return fallback;
                }
                if (validity.stepMismatch) {
                    const fallback = findFromMessages("stepMismatch") || "Invalid step value.";
                    el.setCustomValidity(fallback);
                    return fallback;
                }
            }

            const defaultMsg = "Invalid value.";
            el.setCustomValidity(defaultMsg);
            return defaultMsg;
        }
    }

    return forwardRef(WithErrorMessageComponent as any);
}

export default WithValidationErrorPopup;
